import { useState, useEffect, useCallback, type ReactNode } from 'react';
import { getClientAuditReport } from '../../services/clientService';
import { useProfileStore } from '../../store/useProfileStore';
import { formatDateWithTimezone } from '../../utils/dateUtils';
import type { ClientAuditReportRow, ClientAuditTeamSection } from '../../types/clientAudit';

interface ClientAuditReportProps {
  clientId: string;
  clientName?: string;
}

const CERTIFICATION_LABELS: Record<ClientAuditReportRow['certificationStatus'], string> = {
  not_started: 'Not started',
  in_progress: 'In progress',
  complete: 'Complete',
};

function AuditField({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="min-w-[140px] flex-1">
      <p className="text-xs text-gray-400">{label}</p>
      <div className="text-sm text-gray-900 mt-0.5 break-words">{children}</div>
    </div>
  );
}

function AuditUserRow({ user, timezone }: { user: ClientAuditReportRow; timezone?: string }) {
  return (
    <div className="flex flex-wrap items-start gap-x-6 gap-y-3 px-4 py-3 border-b border-gray-50 hover:bg-gray-50 transition-colors">
      <AuditField label="Display Name">{user.displayName}</AuditField>
      <AuditField label="Email">{user.email}</AuditField>
      <AuditField label="Invite Status">
        {user.inviteStatus === 'pending'
          ? <span className="italic text-orange-500">Pending</span>
          : <span className="text-green-700">Accepted</span>}
      </AuditField>
      <AuditField label="Last Login">
        {user.lastLogin ? formatDateWithTimezone(user.lastLogin, timezone) : '—'}
      </AuditField>
      <AuditField label="Courses Completed">{user.coursesCompleted}</AuditField>
      <AuditField label="Certification %">
        {user.certificationPercent != null ? `${user.certificationPercent}%` : '—'}
      </AuditField>
      <AuditField label="Certification">
        {CERTIFICATION_LABELS[user.certificationStatus] ?? user.certificationStatus}
      </AuditField>
    </div>
  );
}

function TeamAuditSection({
  section,
  page,
  pageSize,
  loading,
  timezone,
  onPageChange,
  onPageSizeChange,
}: {
  section: ClientAuditTeamSection;
  page: number;
  pageSize: number;
  loading: boolean;
  timezone?: string;
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
}) {
  const start = (page - 1) * pageSize;
  const pagedUsers = section.users.slice(start, start + pageSize);
  const hasPrev = page > 1;
  const hasNext = pagedUsers.length === pageSize && start + pageSize < section.users.length;

  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
      <div className="px-4 py-3 border-b border-gray-100">
        <h2 className="text-lg font-bold text-gray-900">{section.teamName}</h2>
      </div>

      <div className="min-h-[120px]">
        {loading ? (
          <div className="px-4 py-12 text-center text-gray-400">Loading...</div>
        ) : pagedUsers.length === 0 ? (
          <div className="px-4 py-12 text-center text-gray-400">No users found</div>
        ) : (
          pagedUsers.map((user) => (
            <AuditUserRow key={user.id} user={user} timezone={timezone} />
          ))
        )}
      </div>

      <div className="flex items-center justify-end gap-4 px-4 py-3 border-t border-gray-100">
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-500">Rows:</span>
          <select
            value={pageSize}
            onChange={(e) => onPageSizeChange(Number(e.target.value))}
            className="text-sm border border-gray-200 rounded px-2 py-1"
          >
            <option value={10}>10</option>
            <option value={25}>25</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
          </select>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => onPageChange(page - 1)}
            disabled={!hasPrev}
            className={`text-sm px-3 py-1 rounded ${hasPrev ? 'text-gray-700 hover:bg-gray-100' : 'text-gray-300 cursor-not-allowed'}`}
          >
            &larr; Previous
          </button>
          <span className="text-sm text-gray-500">Page {page}</span>
          <button
            onClick={() => onPageChange(page + 1)}
            disabled={!hasNext}
            className={`text-sm px-3 py-1 rounded ${hasNext ? 'text-gray-700 hover:bg-gray-100' : 'text-gray-300 cursor-not-allowed'}`}
          >
            Next &rarr;
          </button>
        </div>
      </div>
    </div>
  );
}

export function ClientAuditReport({ clientId, clientName }: ClientAuditReportProps) {
  const { activeProfile } = useProfileStore();
  const [teamSections, setTeamSections] = useState<ClientAuditTeamSection[]>([]);
  const [teamPages, setTeamPages] = useState<Record<string, number>>({});
  const [pageSize, setPageSize] = useState(25);
  const [loading, setLoading] = useState(true);

  const totalUsers = teamSections.reduce((sum, section) => sum + section.users.length, 0);
  const title = clientName ? `Client Audit - ${clientName}` : 'Client Audit';

  const fetch = useCallback(async () => {
    setLoading(true);
    try {
      const sections = await getClientAuditReport(clientId);
      setTeamSections(sections);
      setTeamPages({});
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  useEffect(() => {
    fetch();
  }, [fetch]);

  return (
    <div className="space-y-4">
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
          <h2 className="text-lg font-bold text-gray-900">{title}</h2>
          <button
            onClick={fetch}
            className="p-2 text-gray-400 hover:text-gray-700 rounded-lg hover:bg-gray-100 transition-colors"
            title="Refresh"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 2v6h-6"/>
              <path d="M3 12a9 9 0 0 1 15-6.7L21 8"/>
              <path d="M3 22v-6h6"/>
              <path d="M21 12a9 9 0 0 1-15 6.7L3 16"/>
            </svg>
          </button>
        </div>
        <div className="px-4 py-3 text-sm text-gray-500">
          {loading
            ? 'Loading audit report...'
            : `${totalUsers} user${totalUsers === 1 ? '' : 's'} across ${teamSections.length} team${teamSections.length === 1 ? '' : 's'}`}
        </div>
      </div>

      {!loading && teamSections.length === 0 && (
        <p className="text-sm text-gray-500 bg-white border border-gray-200 rounded-xl px-4 py-6 text-center">
          No teams found for this client.
        </p>
      )}

      {teamSections.map((section) => (
        <TeamAuditSection
          key={section.teamId}
          section={section}
          page={teamPages[section.teamId] ?? 1}
          pageSize={pageSize}
          loading={loading}
          timezone={activeProfile?.timezone}
          onPageChange={(nextPage) => setTeamPages((prev) => ({ ...prev, [section.teamId]: nextPage }))}
          onPageSizeChange={(size) => {
            setPageSize(size);
            setTeamPages({});
          }}
        />
      ))}

    </div>
  );
}
